/**
 * Per-row GAM data readiness.
 *
 * A GAM-linked 1P row's pacing/delivered figures come from a date-ranged GAM
 * report (`delivery.impressionsDeliveredMonth`). Until that report has run for
 * the CURRENT link (re-linking deletes the cached figure), the only numbers
 * available are lifetime sums — overstated for multi-month line items — so the
 * dashboard renders a "Syncing GAM…" state instead of numbers. Unlinked and 3P
 * rows are always "ready": they have their own existing empty/manual states and
 * never wait on GAM.
 */

import { Invoice, isThreeP } from './types';

export type GamSyncState = 'ready' | 'syncing' | 'awaiting' | 'failed';

/** True when nothing GAM-derived is pending for this row. */
export function gamDataReady(inv: Invoice): boolean {
  if (!inv.gam.gamLineItemIds.length) return true;
  if (isThreeP(inv)) return true;
  return inv.delivery.impressionsDeliveredMonth !== undefined;
}

/**
 * The row's sync state. An in-flight refresh wins over a recorded failure
 * (a retry is already running), and a failure only shows when nothing is
 * running — otherwise the row is waiting for the next scheduled sync.
 */
export function gamSyncState(inv: Invoice): GamSyncState {
  if (gamDataReady(inv)) return 'ready';
  if (inv.monthRefreshInFlight) return 'syncing';
  if (inv.delivery.monthDeliveredError) return 'failed';
  return 'awaiting';
}
